import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
} from '@wix/sdk-types';
import * as ambassadorWixBadgesV4Badge from './badges-v4-badge-badges-v-2.http.js';
// @ts-ignore
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';

/**
 * A badge is a visible label displayed on a site member's profile.
 *
 * Site owners can create badges to categorize members and optionally grant them special permissions to access specific pages.
 *
 * Badges are managed separately from their assignment to members. To assign badges to members, use the [Badge Assignments](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/activity/badge-assignments/introduction) API.
 *
 * For more information, see [Creating and Managing Member Badges](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges).
 */
export interface Badge {
  /**
   * Badge ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the badge is updated. To prevent conflicting changes, the current revision must be passed when updating the badge.
   * @readonly
   */
  revision?: string | null;
  /**
   * Badge title displayed on the member's profile.
   * @minLength 1
   * @maxLength 50
   */
  title?: string | null;
  /**
   * Badge description explaining its purpose or criteria for earning it.
   * @maxLength 1500
   */
  description?: string | null;
  /**
   * Badge background color in hexadecimal RGB format.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Badge text color in hexadecimal RGB format.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Badge icon image.
   *
   * Any image format is accepted with automatic conversion and resizing handled by the system.
   * SVG format is recommended as it is resolution independent and looks great at any scale.
   */
  icon?: string;
  /**
   * Whether the badge grants special permissions to access specific members-only pages.
   *
   * When `true`, members with this badge receive special permissions.
   * Site owners can configure these permissions in the site dashboard. For more information, see [Creating and Managing Member Badges](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges).
   */
  permissionsEnabled?: boolean | null;
  /**
   * URL-friendly slug for the badge, automatically generated from the title.
   *
   * Generated by converting the title to lowercase, replacing spaces with hyphens, and removing special characters.
   * @readonly
   * @minLength 1
   * @maxLength 255
   */
  slug?: string | null;
  /**
   * Date and time the badge was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the badge was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

export interface MigrationBadgeUpdated {
  migrationBadge?: MigrationBadge;
}

export interface MigrationBadge {
  /**
   * Badge ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the Badge is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the Badge.
   * Ignored when creating a Badge.
   * @readonly
   */
  revision?: string | null;
  /**
   * Text displayed on the badge in the Wix UI.
   * @minLength 1
   * @maxLength 50
   */
  title?: string | null;
  /**
   * Badge description.
   * @maxLength 1500
   */
  description?: string | null;
  /**
   * Badge background color in hexadecimal RGB format.
   * Uppercase letters only.
   * Example: `#FFFFFF`.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Badge text color in hexadecimal RGB format.
   * Uppercase letters only.
   * Example: `#C81B53`.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Badge icon image.
   * _Recommended_ to use `SVG` image format as it is resolution independent and looks great at any scale.
   */
  icon?: string;
  /**
   * Whether the badge has special permissions
   * to access specific members-only pages.
   * When `true`, members with the badge receive special permissions,
   * and site contributors can
   * [manage badge permissions](https://support.wix.com/en/article/setting-permissions-for-a-member-badge)
   * in the site dashboard.
   * When `false`, members with the badge receive no special permissions.
   */
  permissionsEnabled?: boolean | null;
  /**
   * Slugified name. Used to represent the badge in a URL.
   * @readonly
   * @minLength 1
   * @maxLength 255
   */
  slug?: string | null;
  /**
   * Date and time the Badge was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the Badge was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Order rank of the badge in the site
   * @readonly
   */
  orderRank?: number | null;
}

export interface MigrationBadgeCreated {
  migrationBadge?: MigrationBadge;
}

export interface CreateBadgeRequest {
  /** Badge to create. */
  badge: Badge;
}

export interface CreateBadgeResponse {
  /** Created badge. */
  badge?: Badge;
}

export interface GetBadgeRequest {
  /**
   * Badge ID.
   * @format GUID
   */
  badgeId: string;
}

export interface GetBadgeResponse {
  /** Retrieved badge. */
  badge?: Badge;
}

export interface QueryBadgesRequest {
  /** Query options including filters, sorting, and paging. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /** Name of the field to sort by. */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface QueryBadgesResponse {
  /** List of badges matching the query criteria. */
  badges?: Badge[];
  /** Paging metadata for retrieving additional results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  cursors?: Cursors;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to previous page in the list of results. */
  prev?: string | null;
}

export interface QueryBadgesLegacyRequest {
  /** Filter criteria */
  filter?: Record<string, any> | null;
  /**
   * Sorting criteria
   * @maxSize 1000
   */
  sort?: Sorting[];
  /** Result paging data */
  paging?: Paging;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface QueryBadgesLegacyResponse {
  /** List of sorted badges in a site that satisfy filter and paging */
  badges?: Badge[];
  /** Total count of badges in a site that satisfy given filter */
  metadata?: PagingMetadata;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
}

export interface UpdateBadgeRequest {
  /** Badge to update. Must include ID and revision to prevent conflicts. */
  badge: Badge;
}

export interface UpdateBadgeResponse {
  /** Updated badge with incremented revision and updated timestamp. */
  badge?: Badge;
}

export interface DeleteBadgeRequest {
  /**
   * Badge ID.
   * @format GUID
   */
  badgeId: string;
}

export interface DeleteBadgeResponse {}

export interface UpdateBadgesDisplayOrderRequest {
  /**
   * Ordered list of badge IDs.
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  ids: string[];
}

export interface UpdateBadgesDisplayOrderResponse {
  /** Badges in their new display order. */
  badges?: Badge[];
}

export interface MoveBadgeRequest {
  /**
   * ID of the badge to move.
   * @format GUID
   */
  badgeId: string;
  /** Target position for the badge. */
  position?: PositionWithLiterals;
  /**
   * ID of the badge after which to position your badge.
   * @format GUID
   */
  moveAfterBadgeId?: string | null;
}

export enum Position {
  /** Position the badge at the end of the display sequence. */
  LAST = 'LAST',
  /** Position the badge at the beginning of the display sequence. */
  FIRST = 'FIRST',
  /**
   * Position the badge after another specific badge.
   * In this case you need to specify `moveAfterBadgeId`.
   */
  AFTER = 'AFTER',
}

/** @enumType */
export type PositionWithLiterals = Position | 'LAST' | 'FIRST' | 'AFTER';

export interface MoveBadgeResponse {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type UpdateBadgeValidationErrors = {
  ruleName?: 'BADGE_NOT_PROVIDED';
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface BadgeCreatedEnvelope {
  entity: Badge;
  metadata: EventMetadata;
}

/**
 * Triggered when a badge is created.
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Online Programs
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Badges
 * @permissionScopeId SCOPE.DC-MEMBERS.MANAGE-BADGES
 * @permissionScope Read Badges
 * @permissionScopeId SCOPE.DC-MEMBERS.READ-BADGES
 * @permissionId BADGES.BADGE_READ
 * @webhook
 * @eventType wix.badges.v4.badge_created
 * @serviceIdentifier wix.badges.v4.Badges
 * @slug created
 */
export declare function onBadgeCreated(
  handler: (event: BadgeCreatedEnvelope) => void | Promise<void>
): void;

export interface BadgeDeletedEnvelope {
  metadata: EventMetadata;
}

/**
 * Triggered when a badge is deleted.
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Online Programs
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Badges
 * @permissionScopeId SCOPE.DC-MEMBERS.MANAGE-BADGES
 * @permissionScope Read Badges
 * @permissionScopeId SCOPE.DC-MEMBERS.READ-BADGES
 * @permissionId BADGES.BADGE_READ
 * @webhook
 * @eventType wix.badges.v4.badge_deleted
 * @serviceIdentifier wix.badges.v4.Badges
 * @slug deleted
 */
export declare function onBadgeDeleted(
  handler: (event: BadgeDeletedEnvelope) => void | Promise<void>
): void;

export interface BadgeUpdatedEnvelope {
  entity: Badge;
  metadata: EventMetadata;
}

/**
 * Triggered when a badge is updated.
 * @permissionScope Manage_Wix_Rentals_App_Forms_submissions
 * @permissionScopeId SCOPE.RENTALS.MANAGE
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Online Programs
 * @permissionScopeId SCOPE.CHALLENGES.MANAGE
 * @permissionScope Manage Badges
 * @permissionScopeId SCOPE.DC-MEMBERS.MANAGE-BADGES
 * @permissionScope Read Badges
 * @permissionScopeId SCOPE.DC-MEMBERS.READ-BADGES
 * @permissionId BADGES.BADGE_READ
 * @webhook
 * @eventType wix.badges.v4.badge_updated
 * @serviceIdentifier wix.badges.v4.Badges
 * @slug updated
 */
export declare function onBadgeUpdated(
  handler: (event: BadgeUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a badge.
 * @param badge - Badge to create.
 * @public
 * @requiredField badge
 * @requiredField badge.backgroundColor
 * @requiredField badge.textColor
 * @requiredField badge.title
 * @permissionId BADGES.BADGE_CREATE
 * @applicableIdentity APP
 * @returns Created badge.
 * @fqn wix.badges.v4.Badges.CreateBadge
 */
export async function createBadge(
  badge: NonNullablePaths<Badge, `backgroundColor` | `textColor` | `title`, 2>
): Promise<Badge> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ badge: badge }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'badge.icon' }],
      },
    ]
  );

  const reqOpts = ambassadorWixBadgesV4Badge.createBadge(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'badge.icon' }],
        },
      ])
    )?.badge!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { badge: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['badge']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a badge by ID.
 * @param badgeId - Badge ID.
 * @public
 * @requiredField badgeId
 * @permissionId BADGES.BADGE_READ
 * @applicableIdentity APP
 * @returns Retrieved badge.
 * @fqn wix.badges.v4.Badges.GetBadge
 */
export async function getBadge(badgeId: string): Promise<Badge> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ badgeId: badgeId });

  const reqOpts = ambassadorWixBadgesV4Badge.getBadge(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'badge.icon' }],
        },
      ])
    )?.badge!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { badgeId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['badgeId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a query to retrieve a list of badges.
 *
 * The `queryBadges()` method builds a query to retrieve a list of badges and returns a `BadgesQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
 *
 * You can refine the query by chaining `BadgesQueryBuilder` methods onto the query. `BadgesQueryBuilder` methods enable you to sort, filter, and control the results `queryBadges()` returns.
 *
 * `queryBadges()` runs with these `BadgesQueryBuilder` defaults, which you can override:
 *
 * - limit(50)
 * - descending("_createdDate")
 *
 * The methods that are chained to `queryBadges()` are applied in the order they're called. For example, if you apply ascending('title') and then descending('id'), the results are sorted first by the title, and then, if there are multiple results with the same title, the items are sorted by ID.
 * @public
 * @permissionId BADGES.BADGE_READ
 * @applicableIdentity APP
 * @fqn wix.badges.v4.Badges.QueryBadges
 */
export function queryBadges(): BadgesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[0] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<Badge, 'OFFSET', QueryBadgesRequest, QueryBadgesResponse>(
    {
      func: async (payload: QueryBadgesRequest) => {
        const reqOpts = ambassadorWixBadgesV4Badge.queryBadges(payload);

        sideEffects?.onSiteCall?.();
        try {
          const result = await httpClient.request(reqOpts);
          sideEffects?.onSuccess?.(result);
          return result;
        } catch (err) {
          sideEffects?.onError?.(err);
          throw err;
        }
      },
      requestTransformer: (query: QueryBadgesRequest['query']) => {
        const args = [query, {}] as [QueryBadgesRequest['query'], {}];
        return renameKeysFromSDKRequestToRESTRequest({
          ...args?.[1],
          query: args?.[0],
        });
      },
      responseTransformer: ({ data }: HttpResponse<QueryBadgesResponse>) => {
        const transformedData = renameKeysFromRESTResponseToSDKResponse(
          transformPaths(data, [
            {
              transformFn: transformRESTImageToSDKImage,
              paths: [{ path: 'badges.icon' }],
            },
          ])
        );

        return {
          items: transformedData?.badges,
          pagingMetadata: transformedData?.pagingMetadata,
        };
      },
      errorTransformer: (err: unknown) => {
        const transformedError = sdkTransformError(err, {
          spreadPathsToArguments: {},
          explicitPathsToArguments: { query: '$[0]' },
          singleArgumentUnchanged: false,
        });

        throw transformedError;
      },
      pagingMethod: 'OFFSET',
      transformationPaths: {},
    }
  );
}

interface QueryOffsetResult {
  currentPage: number | undefined;
  totalPages: number | undefined;
  totalCount: number | undefined;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface BadgesQueryResult extends QueryOffsetResult {
  items: Badge[];
  query: BadgesQueryBuilder;
  next: () => Promise<BadgesQueryResult>;
  prev: () => Promise<BadgesQueryResult>;
}

export interface BadgesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'permissionsEnabled'
      | 'slug',
    value: any
  ) => BadgesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'permissionsEnabled'
      | 'slug',
    value: any
  ) => BadgesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName: '_id' | 'title' | 'description' | 'slug',
    value: any
  ) => BadgesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName: '_id' | 'title' | 'description' | 'slug',
    value: any
  ) => BadgesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName: '_id' | 'title' | 'description' | 'slug',
    value: any
  ) => BadgesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName: '_id' | 'title' | 'description' | 'slug',
    value: any
  ) => BadgesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName: '_id' | 'title' | 'description' | 'slug',
    value: string
  ) => BadgesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'permissionsEnabled'
      | 'slug',
    value: any[]
  ) => BadgesQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'permissionsEnabled'
      | 'slug',
    value: any
  ) => BadgesQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | 'title'
      | 'description'
      | 'permissionsEnabled'
      | 'slug',
    value: boolean
  ) => BadgesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      '_id' | 'title' | 'description' | 'permissionsEnabled' | 'slug'
    >
  ) => BadgesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => BadgesQueryBuilder;
  /** @param skip - Number of items to skip in the query results before returning the results. */
  skip: (skip: number) => BadgesQueryBuilder;
  find: () => Promise<BadgesQueryResult>;
}

/**
 * @hidden
 * @fqn wix.badges.v4.Badges.QueryBadges
 * @requiredField query
 */
export async function typedQueryBadges(
  query: BadgeQuery
): Promise<NonNullablePaths<QueryBadgesResponse, `badges`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixBadgesV4Badge.queryBadges(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'badges.icon' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BadgeQuerySpec extends QuerySpec {
  paging: 'offset';
  wql: [
    {
      fields: ['_id', 'description', 'permissionsEnabled', 'slug', 'title'];
      operators: '*';
      sort: 'ASC';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<Badge, BadgeQuerySpec>;
export type BadgeQuery = {
  /** 
  Filter object in the following format:
  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`
  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`  
  */
  sort?: {
    /** 
  Name of the field to sort by.  
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Badge, BadgeQuerySpec, BadgeQuery>(),
  },
};

/**
 * Updates a badge.
 * @param _id - Badge ID.
 * @public
 * @requiredField _id
 * @requiredField badge
 * @permissionId BADGES.BADGE_UPDATE
 * @applicableIdentity APP
 * @returns Updated badge with incremented revision and updated timestamp.
 * @fqn wix.badges.v4.Badges.UpdateBadge
 */
export async function updateBadge(
  _id: string,
  badge: UpdateBadge
): Promise<
  Badge & {
    __validationErrorsType?: UpdateBadgeValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ badge: { ...badge, id: _id } }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [{ path: 'badge.icon' }],
      },
    ]
  );

  const reqOpts = ambassadorWixBadgesV4Badge.updateBadge(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'badge.icon' }],
        },
      ])
    )?.badge!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { badge: '$[1]' },
        explicitPathsToArguments: { 'badge.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'badge']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateBadge {
  /**
   * Badge ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Revision number, which increments by 1 each time the badge is updated. To prevent conflicting changes, the current revision must be passed when updating the badge.
   * @readonly
   */
  revision?: string | null;
  /**
   * Badge title displayed on the member's profile.
   * @minLength 1
   * @maxLength 50
   */
  title?: string | null;
  /**
   * Badge description explaining its purpose or criteria for earning it.
   * @maxLength 1500
   */
  description?: string | null;
  /**
   * Badge background color in hexadecimal RGB format.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Badge text color in hexadecimal RGB format.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Badge icon image.
   *
   * Any image format is accepted with automatic conversion and resizing handled by the system.
   * SVG format is recommended as it is resolution independent and looks great at any scale.
   */
  icon?: string;
  /**
   * Whether the badge grants special permissions to access specific members-only pages.
   *
   * When `true`, members with this badge receive special permissions.
   * Site owners can configure these permissions in the site dashboard. For more information, see [Creating and Managing Member Badges](https://support.wix.com/en/article/site-members-creating-and-managing-member-badges).
   */
  permissionsEnabled?: boolean | null;
  /**
   * URL-friendly slug for the badge, automatically generated from the title.
   *
   * Generated by converting the title to lowercase, replacing spaces with hyphens, and removing special characters.
   * @readonly
   * @minLength 1
   * @maxLength 255
   */
  slug?: string | null;
  /**
   * Date and time the badge was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the badge was last updated.
   * @readonly
   */
  _updatedDate?: Date | null;
}

/**
 * Deletes a badge.
 *
 * Deleting a badge will remove it from all members who currently have it assigned.
 * @param badgeId - Badge ID.
 * @public
 * @requiredField badgeId
 * @permissionId BADGES.BADGE_DELETE
 * @applicableIdentity APP
 * @fqn wix.badges.v4.Badges.DeleteBadge
 */
export async function deleteBadge(badgeId: string): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ badgeId: badgeId });

  const reqOpts = ambassadorWixBadgesV4Badge.deleteBadge(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { badgeId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['badgeId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates the display order of badges.
 *
 * Provide an ordered array of badge IDs to set their display sequence.
 *
 * This affects how badges appear to site members and in the site dashboard.
 * @param ids - Ordered list of badge IDs.
 * @public
 * @requiredField ids
 * @permissionId BADGES.BADGE_UPDATE_DISPLAY_ORDER
 * @applicableIdentity APP
 * @fqn wix.badges.v4.Badges.UpdateBadgesDisplayOrder
 * @deprecated
 * @replacedBy wix.badges.v4.Badges.MoveBadge
 * @targetRemovalDate 2026-07-01
 */
export async function updateBadgesDisplayOrder(
  ids: string[]
): Promise<NonNullablePaths<UpdateBadgesDisplayOrderResponse, `badges`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ ids: ids });

  const reqOpts = ambassadorWixBadgesV4Badge.updateBadgesDisplayOrder(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'badges.icon' }],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { ids: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['ids']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Moves a badge to a specific position in the display order.
 *
 * You can position a badge first, last, or immediately after another badge.
 *
 * This affects how badges appear to site members and in the site dashboard.
 * @param badgeId - ID of the badge to move.
 * @public
 * @requiredField badgeId
 * @permissionId BADGES.BADGE_UPDATE_DISPLAY_ORDER
 * @applicableIdentity APP
 * @fqn wix.badges.v4.Badges.MoveBadge
 */
export async function moveBadge(
  badgeId: string,
  options?: MoveBadgeOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    badgeId: badgeId,
    position: options?.position,
    moveAfterBadgeId: options?.moveAfterBadgeId,
  });

  const reqOpts = ambassadorWixBadgesV4Badge.moveBadge(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          badgeId: '$[0]',
          position: '$[1].position',
          moveAfterBadgeId: '$[1].moveAfterBadgeId',
        },
        singleArgumentUnchanged: false,
      },
      ['badgeId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface MoveBadgeOptions {
  /** Target position for the badge. */
  position?: PositionWithLiterals;
  /**
   * ID of the badge after which to position your badge.
   * @format GUID
   */
  moveAfterBadgeId?: string | null;
}
